import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from './BaseComponent';
import { LayoutType } from '../constants/LayoutConstants';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { ViewSettingsActions } from '../actions/ViewSettingsActions';
import { SceneStore } from '../stores/SceneStore';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { ArticleStore } from '../stores/ArticleStore';
import { ScreenshotHelper } from '../utils/ScreenshotHelper';
import { CameraSelect } from './popupmenu/CameraSelect';
import { ToolbarButton } from './ToolbarButton';
import { ToolbarButtonAR } from './ToolbarButtonAR';
import { ArStore } from '../stores/ArStore';

interface ButtonItem {
    element: JSX.Element;
    width: number;
}

export interface ToolbarButtonConfig {
    elements: Array<JSX.Element>;
    width: number;
    extraButtons: Array<JSX.Element>;
    fits: boolean;
}

interface ToolbarState {
    extraButtons: boolean;
    activeView: HTMLDivElement | null;
    elementSelected: boolean;
    architectureElementSelected: boolean;
    canRedo: boolean;
    canUndo: boolean;
    hiddenDueOrientationChange: boolean;
    arDevice: boolean;
}

interface ToolbarProps {
    layoutType: LayoutType;
    elementsInView: boolean;
}

export class Toolbar extends BaseComponent<ToolbarProps, ToolbarState> {
    protected orientationChangeTimeout: number = 0;

    public componentWillMount(): void {
        this.addStoreListener(ArticleStore, this.onChange);
        this.addStoreListener(ProjectManagerStore, this.onChange);
        this.addStoreListener(SceneStore, this.onChange);
        this.addStoreListener(ViewSettingsStore, this.onChange);
        this.addStoreListener(ArStore, this.onChange);
        this.onChange();
        this.setState({hiddenDueOrientationChange: false});
    }

    public componentDidMount(): void {
        window.addEventListener('orientationchange', this.orientationChange);
    }

    public componentDidUpdate(): void {
        window.clearTimeout(this.orientationChangeTimeout);
        window.removeEventListener('orientationchange', this.orientationChange);
    }

    public render(): JSX.Element | null {
        if (this.state.hiddenDueOrientationChange) {
            return null;
        }

        const tContent: ToolbarButtonConfig = this.toolbarButtons();

        let toolbarStyle: React.CSSProperties | undefined;

        if (this.props.layoutType !== LayoutType.SMALL) {
            toolbarStyle = {
                width: tContent.width,
                maxWidth: tContent.width
            };
        }

        return (
            <div className="core-toolbar" style={toolbarStyle}>
                {tContent.elements}
                {/* tslint:disable-next-line:max-line-length */}
                {tContent.extraButtons.length && this.state.extraButtons ? <div className="extra-buttons with-popupflag top-arrow align-right">{tContent.extraButtons}</div> : null}
            </div>
        );
    }

    protected orientationChange = (): void => {
        this.setState({hiddenDueOrientationChange: true});
        this.orientationChangeTimeout = window.setTimeout(this.showToolbar, 100);
    };

    protected showToolbar = (): void => {
        requestAnimationFrame(() => {
            this.setState({hiddenDueOrientationChange: false});
        });
    };

    protected onChange = (): void => {
        this.setState({
            extraButtons: ViewSettingsStore.getState().extraButtons,
            activeView: SceneStore.getState().activeView,
            elementSelected: SceneStore.articleSelected(),
            architectureElementSelected: SceneStore.architectureObjectSelected(),
            canUndo: SceneStore.getState().canUndo,
            canRedo: SceneStore.getState().canRedo,
            arDevice: ArStore.getState().arDevice
        });
    };

    protected toggleExtraButtons = () => {
        if (this.state.extraButtons) {
            ViewSettingsActions.hideExtraButtons();
        } else {
            ViewSettingsActions.showExtraButtons();
        }
    };

    protected getButtons = (): Array<ButtonItem> => {
        const buttons: Array<ButtonItem> = [];
        if (this.props.layoutType !== LayoutType.SMALL) {
            if (this.state.arDevice) {
                buttons.push({
                    element: (
                        <ToolbarButtonAR name="ar-mode" key="ar-button" disabled={!this.props.elementsInView} label={t('AR mode')} />
                    ),
                    width: 42
                });
            }
            buttons.push({element: <CameraSelect key="cameraselect" layoutType={this.props.layoutType}/>, width: 56});
            if (false) {
                // tslint:disable-next-line:max-line-length
                buttons.push({element: <ToolbarButton name="snapshot" key="snapshot-button" disabled={!this.props.elementsInView} label={t('Snapshot')}/>, width: 42});
                // tslint:disable-next-line:max-line-length
                buttons.push({element: <ToolbarButton name="send" key="send-button" disabled={!this.props.elementsInView} label={t('Save')}/>, width: 42});
            } else if (ScreenshotHelper.screenshotAllowed()) {
                // tslint:disable-next-line:max-line-length
                buttons.push({element: <ToolbarButton name="download-image" key="download-button" disabled={!this.props.elementsInView} label={t('Snapshot')}/>, width: 42});
            }
            return buttons;
        }

        buttons.push({ element: <ToolbarButton name="cam-perspective" key="cam-perspective-button" label={t('Perspective')} />, width: 42 });
        buttons.push({ element: <ToolbarButton name="cam-top" key="cam-top-button" label={t('Top')} />, width: 42 });
        if (false) {
            // tslint:disable-next-line:max-line-length
            buttons.push({element: <ToolbarButton name="snapshot" key="snapshot-button" disabled={!this.props.elementsInView} label={t('Snapshot')}/>, width: 42});
        } else if (ScreenshotHelper.screenshotAllowed()) {
            // tslint:disable-next-line:max-line-length
            buttons.push({element: <ToolbarButton name="download-image" key="download-button" disabled={!this.props.elementsInView} label={t('Snapshot')}/>, width: 42});
        }
        return buttons;
    };

    protected toolbarButtons = (): ToolbarButtonConfig => {
        const elements: Array<JSX.Element> = [];
        const buttonsToAdd: Array<ButtonItem> = this.getButtons();
        let tSpace: number = 0;
        let separatorPadding: number = 0;
        let tButtonCounter: number = 0;
        let separatorStyle: React.CSSProperties = {};
        let width: number = 0;
        let extraButtonPosition: number = 0;
        let availableSpace: number;
        if (this.props.layoutType === LayoutType.DESKTOP) {
            availableSpace = window.innerWidth - 56;
        } else {
            availableSpace = window.innerWidth - 46;
        }

        for (const tButton of buttonsToAdd) {
            const buttonWidth: number = tButton.width;
            if (tSpace > 0) {
                tSpace += 1; // ((this.props.layoutType !== LayoutType.SMALL && false) ? 3 : 5);
            }
            tSpace += buttonWidth;
            tButtonCounter += 1;
            if (tSpace < availableSpace) {
                if (tButtonCounter > 1) {
                    if (AppConfigurationStore.getState().touchDevice) {
                        separatorPadding = (((availableSpace + 0.5 * buttonWidth) - tSpace) / (tButtonCounter - 1)) * 0.5;
                    } else {
                        separatorPadding = (((availableSpace + buttonWidth) - tSpace) / (tButtonCounter - 1)) * 0.5;
                    }
                }
                width = tSpace;
            } else {
                extraButtonPosition = tButtonCounter - 2;
                width = availableSpace;
                separatorStyle = {paddingLeft: separatorPadding.toString() + 'px', paddingRight: separatorPadding.toString() + 'px'};
                break;
            }
        }
        let tCounter: number = 0;
        const extraButtons: Array<JSX.Element> = [];
        let extraButtonMode: boolean = false;
        for (const tButton of buttonsToAdd) {
            tCounter += 1;
            if (tCounter === extraButtonPosition && !AppConfigurationStore.getState().touchDevice) {
                elements.push(
                    <div className="toolbar-separator" style={separatorStyle} key={'separator-' + elements.length.toString()}><span/></div>
                );
                elements.push(
                    <div className="toolbar-button toolbar-button-extra" onClick={this.toggleExtraButtons} key="extra-button"/>
                );
                extraButtonMode = true;
            }
            if (extraButtonMode) {
                extraButtons.push(tButton.element);
            } else {
                if (elements.length) {
                    elements.push(<div className="toolbar-separator" style={separatorStyle} key={'separator-' + elements.length.toString()}><span/></div>);
                }
                elements.push(tButton.element);
            }
        }

        return {
            elements,
            width,
            extraButtons,
            fits: availableSpace >= width
        };
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Toolbar.tsx